import { Controller } from '@hotwired/stimulus';

/**
 * Generic horizontal carousel that drives a scroll-snap track via two
 * arrow chips. Used by Section 4 (news) and Section 6 (instagram strip).
 *
 * Targets:
 *  - track  the scrolling element
 *  - prev   "previous" chip (disabled at scrollLeft 0)
 *  - next   "next" chip (disabled at the right edge)
 *
 * Values:
 *  - cardSelector  CSS selector matching one card inside the track.
 *                  The controller scrolls by exactly one card width + gap.
 *  - loop          if true, wraps around the ends instead of disabling.
 */
export default class extends Controller {
    static targets = ['track', 'prev', 'next'];
    static values = {
        cardSelector: { type: String, default: '*' },
        loop: { type: Boolean, default: false },
    };

    static SCROLL_DURATION_MS = 260;

    connect() {
        if (!this.hasTrackTarget) return;
        this.onScroll = this.onScroll.bind(this);
        this.trackTarget.addEventListener('scroll', this.onScroll, { passive: true });
        // Card widths are fluid, so a resize can move the content edge without a scroll.
        window.addEventListener('resize', this.onScroll, { passive: true });
        // Honor reduced-motion (use instant jumps instead of tweens).
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Defer one frame so layout has settled before we measure.
        requestAnimationFrame(() => {
            this.updateChipState();
            this.updateOutgoing();
        });
    }

    disconnect() {
        this.trackTarget?.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    }

    next() {
        const step = this.stepSize();
        const track = this.trackTarget;
        if (this.loopValue && this.atRightEdge()) {
            this.tweenScroll(0);
            return;
        }
        this.tweenScroll(track.scrollLeft + step);
    }

    prev() {
        const step = this.stepSize();
        const track = this.trackTarget;
        if (this.loopValue && this.atLeftEdge()) {
            this.tweenScroll(track.scrollWidth);
            return;
        }
        this.tweenScroll(track.scrollLeft - step);
    }

    // Custom tween — native `behavior: 'smooth'` runs ~500ms and feels sluggish.
    // 260ms ease-out matches the snap-mandatory cadence and lets rapid arrow clicks chain.
    tweenScroll(target) {
        const track = this.trackTarget;
        const max = track.scrollWidth - track.clientWidth;
        const to = Math.max(0, Math.min(max, target));
        if (this.prefersReducedMotion) {
            track.scrollLeft = to;
            return;
        }
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
        const from = track.scrollLeft;
        const dist = to - from;
        if (dist === 0) return;
        // Snap-mandatory hijacks intermediate writes to the next snap point —
        // disable while we tween, restore at the end so the rest position is snapped.
        track.style.scrollSnapType = 'none';
        const start = performance.now();
        const duration = this.constructor.SCROLL_DURATION_MS;
        const step = (now) => {
            const t = Math.min(1, (now - start) / duration);
            // easeOutQuart — fast start, gentle settle into the snap.
            const eased = 1 - Math.pow(1 - t, 4);
            track.scrollLeft = from + dist * eased;
            if (t < 1) {
                this.scrollRaf = requestAnimationFrame(step);
            } else {
                this.scrollRaf = null;
                track.style.scrollSnapType = '';
            }
        };
        this.scrollRaf = requestAnimationFrame(step);
    }

    onScroll() {
        this.updateChipState();
        this.updateOutgoing();
    }

    /**
     * Tag the card straddling the track's right edge so CSS can drop it to 50%.
     * Cards fully past the edge are clipped by the scroll container anyway; this
     * is the half-visible one the Figma row renders faded. No-op on full-bleed
     * tracks — those dissolve into the band via a CSS mask instead.
     */
    updateOutgoing() {
        const track = this.trackTarget;
        if (!track.classList.contains('gm-carousel-track--capped')) return;
        const edge = track.getBoundingClientRect().right;
        track.querySelectorAll(this.cardSelectorValue).forEach((card) => {
            const box = card.getBoundingClientRect();
            card.classList.toggle('is-outgoing', box.right > edge + 1 && box.left < edge - 1);
        });
    }

    stepSize() {
        const first = this.trackTarget.querySelector(this.cardSelectorValue);
        if (!first) return this.trackTarget.clientWidth * 0.8;
        const rect = first.getBoundingClientRect();
        // Approximate the gap from computed style; fall back to 16 px.
        const gapPx = parseFloat(getComputedStyle(this.trackTarget).columnGap || getComputedStyle(this.trackTarget).gap || '16');
        return rect.width + (Number.isNaN(gapPx) ? 16 : gapPx);
    }

    atLeftEdge() {
        return this.trackTarget.scrollLeft <= 1;
    }

    atRightEdge() {
        const t = this.trackTarget;
        return t.scrollLeft + t.clientWidth >= t.scrollWidth - 1;
    }

    updateChipState() {
        if (this.loopValue) return;

        if (this.hasPrevTarget) {
            this.prevTarget.disabled = this.atLeftEdge();
            this.prevTarget.setAttribute('aria-disabled', String(this.atLeftEdge()));
        }
        if (this.hasNextTarget) {
            this.nextTarget.disabled = this.atRightEdge();
            this.nextTarget.setAttribute('aria-disabled', String(this.atRightEdge()));
        }
    }
}
